import { DefaultButton, type ICommandBarItemProps, PrimaryButton } from "@fluentui/react";
import ConstructionStatus from "../../../types/ConstructionStatus";
import type ConstructionListItem from "../types/ConstructionListItem";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../services/auth/hooks/useAuth";
import IsaacTagDialogType from "../../constructionsNew/components/tags/types/IsaacTagDialogType";
import PathConstants from "../../../routes/pathConstants";

const AddBulkEditButton = (
  selectedConstructions: ConstructionListItem[],
  handleBulkUpdateTrigger: () => void,
  isAllowToTakeAction: boolean,
  commandBarItems: ICommandBarItemProps[],
): void => {
  const isAllowedToEdit =
    isAllowToTakeAction && selectedConstructions?.every(c => c.status !== ConstructionStatus.Accepted);

  if (selectedConstructions.length > 0 && isAllowedToEdit) {
    const isBulk = selectedConstructions.length !== 1;
    commandBarItems.push({
      key: "Edit",
      type: "button",
      onRender: () => (
        <PrimaryButton
          aria-label="Edit construction"
          text={isBulk ? "Bulk edit" : "Edit"}
          iconProps={{
            iconName: isBulk ? "TripleColumnEdit" : "SingleColumnEdit",
          }}
          onClick={handleBulkUpdateTrigger}
        />
      ),
      disabled: false,
    });
  }
};

const useConstructionsNewCommandBarItems = (
  handleTagDialogDismiss: (type: string) => void,
  handleDeleteConfirmationModalTrigger: () => void,
  handleCalculationNavigationTrigger: (constructionId: string) => void,
  handleBulkUpdateTrigger: () => void,
): ((selectedConstructions: ConstructionListItem[]) => ICommandBarItemProps[]) => {
  const navigate = useNavigate();
  const {user} = useAuth();
  const isAllowToTakeAction = user?.isAllowedToTakeAction();

  const AddDeleteButton = (selectedConstructions: ConstructionListItem[], commandBarItems: ICommandBarItemProps[]): void => {
    if (selectedConstructions.length > 0) {
      const isAllowedToDelete =
        isAllowToTakeAction && selectedConstructions?.every(c => c.status !== ConstructionStatus.Accepted);
      if (isAllowedToDelete) {
        commandBarItems.push({
          key: "Delete",
          type: "button",
          onRender: () => (
            <PrimaryButton
              aria-label="Delete construction"
              text="Delete"
              iconProps={{
                iconName: "Delete",
              }}
              onClick={handleDeleteConfirmationModalTrigger}
            />
          ),
          disabled: false,
        });
      }
    }
  };

  const AddViewCalculationResultsButton = (
      selectedConstructions: ConstructionListItem[],
      commandBarItems: ICommandBarItemProps[],
    ): void => {
      if (selectedConstructions.length === 1) {
        commandBarItems.push({
          key: "calculateResults",
          type: "button",
          onRender: () => (
            <PrimaryButton
              aria-label="View Calculation Result"
              text="View Calculation Result"
              iconProps={{
                iconName: "Calculator",
              }}
              onClick={() => {
                handleCalculationNavigationTrigger(selectedConstructions[0].id);
              }}
            />
          ),
          disabled: false,
        });
      }
    };

    const AddSelectConstructionButton = (selectedConstructions: ConstructionListItem[], commandBarItems: ICommandBarItemProps[]): void => {
        if (selectedConstructions.length <= 0) {
          commandBarItems.push({
            key: "Actions",
            onRender: () => (
              <DefaultButton
                aria-label="Constructions actions"
                text="Select one or more constructions for action bar to show"
                iconProps={{
                  iconName: "SetAction",
                }}
                disabled={true}
              />
            ),
          });
        }
      };

     const AddManageTagsButton = (selectedConstructions: ConstructionListItem[], commandBarItems: ICommandBarItemProps[]): void => {
        if (isAllowToTakeAction) {
            if (selectedConstructions.length >= 1) {
                commandBarItems.push({
                key: "ManageTags",
                onRender: () => (
                    <PrimaryButton
                    aria-label="Constructions actions"
                    text="Manage Tags"
                    iconProps={{
                        iconName: "Tag",
                    }}
                    menuProps={{
                        items: [
                        {
                            key: "emailMessage",
                            text: "Add tags",
                            iconProps: {iconName: "Add"},
                            onClick: () => {
                            handleTagDialogDismiss(IsaacTagDialogType.ADD);
                            },
                        },
                        {
                            key: "calendarEvent",
                            text: "Remove tags",
                            iconProps: {iconName: "Remove"},
                            onClick: () => {
                            handleTagDialogDismiss(IsaacTagDialogType.DELETE);
                            },
                        },
                        ],
                    }}
                    />
                ),
                });
            }
        }
    };

    const AddViewDetailsButton = (selectedConstructions: ConstructionListItem[], commandBarItems: ICommandBarItemProps[]): void => {
        if (selectedConstructions.length === 1) {
        commandBarItems.push({
            key: "Details",
            onRender: () => (
            <PrimaryButton
                aria-label="view details of construction"
                text="Details"
                iconProps={{
                iconName: "View",
                }}
                onClick={() => {
                void navigate(`${PathConstants.Constructions}/${selectedConstructions[0].id}`);
                }}
            />
            ),
            disabled: false,
        });
        }
    };

    const getItems = (selectedConstructions: ConstructionListItem[]): ICommandBarItemProps[] => {
        const commandBarItems: ICommandBarItemProps[] = [];
    
        AddSelectConstructionButton(selectedConstructions, commandBarItems);
        AddViewDetailsButton(selectedConstructions, commandBarItems);
        AddDeleteButton(selectedConstructions, commandBarItems);
        AddViewCalculationResultsButton(selectedConstructions, commandBarItems);
        AddManageTagsButton(selectedConstructions, commandBarItems);
        AddBulkEditButton(selectedConstructions, handleBulkUpdateTrigger, isAllowToTakeAction, commandBarItems);
    
        return commandBarItems;
    };
    
    return getItems;
};

export default useConstructionsNewCommandBarItems;